import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, DataTable, EmptyState, PageHeader, SkeletonTable } from "@/components/ui";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  memberPickerCapped,
  MEMBER_PICKER_PARAMS,
  useCompanyMembers,
} from "@/hooks/use-company-members";
import {
  companyKitRights,
  kitHolderOptions,
  useCompanyTestKits,
  type KitListParams,
  type TestKit,
} from "@/hooks/use-company-test-kits";
import { buildKitColumns } from "@/components/kits/kit-columns";
import {
  emptyKitFilters,
  KitFilterBar,
  type KitFilterState,
} from "@/components/kits/kit-filter-bar";

export const Route = createFileRoute("/_company/company/kits/")({
  component: KitsPage,
});

// The company kit list op caps limit at 100. We fetch the full first page and
// run the search / status / dueBefore filtering client-side so their result
// count is honest across the whole set rather than per-page. The HOLDER axis is
// the exception: it is a wire parameter, so it narrows the read itself.
const FETCH_LIMIT = 100;

const KIT_COLUMN_COUNT = 8;

function matchesSearch(kit: TestKit, q: string): boolean {
  if (q === "") return true;
  const needle = q.trim().toLowerCase();
  return (
    kit.serial_number.toLowerCase().includes(needle) ||
    kit.manufacturer.toLowerCase().includes(needle)
  );
}

function matchesStatus(kit: TestKit, status: KitFilterState["status"]): boolean {
  if (status === "") return true;
  return kit.status === status;
}

function matchesDueBefore(kit: TestKit, dueBefore: string): boolean {
  if (dueBefore === "") return true;
  const expiry = kit.latest_calibration?.calibration_expires_at;
  // No calibration on record is itself "due" — it surfaces under any cutoff.
  if (!expiry) return true;
  return expiry <= dueBefore;
}

function KitsPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const context = useMyCompanyContext();
  const selfProfileId = context.data?.profile.id ?? "";

  const [filters, setFilters] = useState<KitFilterState>(emptyKitFilters);

  // The roster feeds the holder picker. Every live member may read it, so the
  // narrowing is available to whoever is looking at the registry — including a
  // tester asking which gauges they are carrying.
  const membersQuery = useCompanyMembers(companyId, MEMBER_PICKER_PARAMS);
  const holders = useMemo(
    () => kitHolderOptions(membersQuery.data?.items ?? []),
    [membersQuery.data?.items],
  );
  const holdersCapped = memberPickerCapped(membersQuery.data?.items);

  // Fetch the full first page, narrowed by the holder axis on the wire; the
  // other three sift it (manualSorting=false lets DataTable sort the filtered
  // set in-memory).
  const listParams = useMemo<KitListParams>(
    () =>
      filters.holderProfileId === ""
        ? { limit: FETCH_LIMIT }
        : { limit: FETCH_LIMIT, holder_profile_id: filters.holderProfileId },
    [filters.holderProfileId],
  );
  const listQuery = useCompanyTestKits(companyId, listParams);

  const allItems = useMemo(() => listQuery.data?.items ?? [], [listQuery.data?.items]);
  const visibleItems = useMemo(
    () =>
      allItems.filter(
        (kit) =>
          matchesStatus(kit, filters.status) &&
          matchesSearch(kit, filters.q) &&
          matchesDueBefore(kit, filters.dueBefore),
      ),
    [allItems, filters.status, filters.q, filters.dueBefore],
  );

  const openDetail = (kit: TestKit) =>
    void navigate({ to: "/company/kits/$kitId", params: { kitId: kit.id } });
  const openEdit = (kit: TestKit) =>
    void navigate({ to: "/company/kits/$kitId/edit", params: { kitId: kit.id } });

  const columns = useMemo(
    () =>
      buildKitColumns(t, {
        onView: openDetail,
        onEdit: openEdit,
        // Per ROW, not per page: an admin edits every gauge the company owns, a
        // member only the ones they hold.
        canEdit: (kit) => companyKitRights(kit, { isAdmin, profileId: selfProfileId }).canEdit,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the two openers close over the stable navigate
    [t, isAdmin, selfProfileId],
  );

  // total_count > fetched length means the registry holds more than one page;
  // surface a non-silent notice rather than truncating without warning.
  const totalCount = listQuery.data?.total_count ?? 0;
  const isTruncated = totalCount > allItems.length;
  // An empty response under a holder filter means "nobody is carrying anything
  // that matches", not "the registry is empty" — so it must not offer the
  // create CTA that belongs to a genuinely empty registry.
  const isRegistryEmpty = allItems.length === 0 && filters.holderProfileId === "";

  if (!active) return null;

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("company:kits.title")}
        subtitle={t("company:kits.subtitle", { company: active.name })}
        actions={
          // Adding a gauge to the inventory is admin-only. The registry reads
          // for every member, and EDITING one is gated per row in the table.
          isAdmin ? (
            <Button onClick={() => void navigate({ to: "/company/kits/new" })}>
              {t("company:kits.newKit")}
            </Button>
          ) : undefined
        }
      />
      <KitFilterBar
        value={filters}
        onChange={setFilters}
        holders={holders}
        holdersCapped={holdersCapped}
      />
      {listQuery.isLoading ? (
        <SkeletonTable columns={KIT_COLUMN_COUNT} />
      ) : isRegistryEmpty ? (
        // True-empty: no kits at all → offer the create CTA, to an admin.
        <EmptyState
          title={t("company:kits.empty")}
          actionLabel={isAdmin ? t("company:kits.newKit") : undefined}
          onAction={isAdmin ? () => void navigate({ to: "/company/kits/new" }) : undefined}
        />
      ) : visibleItems.length === 0 ? (
        // Filtered-empty: kits exist but the active filters hide them all —
        // no create CTA, just guidance to relax the filters.
        <EmptyState title={t("company:kits.noMatches")} />
      ) : (
        <>
          {isTruncated && <p className="text-sm text-muted">{t("company:kits.truncated")}</p>}
          <DataTable
            columns={columns}
            data={visibleItems}
            manualSorting={false}
            onRowClick={openDetail}
          />
        </>
      )}
    </div>
  );
}
